import {ArrowRight, Shield, Wrench, HardHat, Star} from 'lucide-react';
import { Link } from "react-router-dom";
import { useSeoMeta } from '@unhead/react';

const entryRoles = [
  {
    icon: "🚛",
    title: "Haul Truck Operator",
    description: "Drive massive haul trucks in open-cut mines. Full ticket training provided on-site.",
    salary: "$80K–$95K",
    trainingTime: "4 weeks",
    fifo: true,
    requirements: ["HR or HC licence preferred", "18+ years old", "Physical fitness test", "Drug & alcohol clearance"],
  },
  {
    icon: "🔩",
    title: "Mine Site Labourer",
    description: "General labouring duties — site clean-up, maintenance support, equipment assistance.",
    salary: "$70K–$82K",
    trainingTime: "1 week",
    fifo: true,
    requirements: ["No experience required", "White Card (Construction)", "Steel cap boots", "Willingness to learn"],
  },
  {
    icon: "⛽",
    title: "Fuel & Lube Attendant",
    description: "Service and fuel heavy mining equipment. One of the best entry points into mining.",
    salary: "$78K–$90K",
    trainingTime: "2 weeks",
    fifo: false,
    requirements: ["Driver's licence", "DG awareness (preferred)", "Mechanical aptitude", "18+ years old"],
  },
  {
    icon: "🌱",
    title: "Trainee Miner",
    description: "Structured traineeship covering drill, blast, load and haul across a 12-month program.",
    salary: "$72K–$88K",
    trainingTime: "12 months",
    fifo: false,
    requirements: ["Year 10 completion", "Cert II pathway included", "Must pass pre-employment medical", "No prior experience needed"],
  },
];

const steps = [
  { step: "01", title: "White Card", desc: "Complete your General Construction Induction (White Card) online. Takes 6 hours.", cost: "$75–$120" },
  { step: "02", title: "Pre-Employment Medical", desc: "Pass a standard mine site medical including hearing, vision and functional capacity.", cost: "$150–$280" },
  { step: "03", title: "Drug & Alcohol Test", desc: "Urine and/or hair follicle test. Stay clean for 90 days prior to application.", cost: "Employer-paid" },
  { step: "04", title: "Apply & Get Hired", desc: "Apply directly through MineralJobs. Entry-level roles fill fast — set job alerts!", cost: "Free" },
];

export default function EntryLevel() {
  useSeoMeta({
    title: 'Entry Level Mining Jobs — No Experience Needed | MineralJobs',
    description: 'Browse entry level and inexperienced mining jobs in Australia. FIFO roles with full training provided. Start your mining career today.',
    ogTitle: 'Entry Level Mining Jobs | MineralJobs',
    ogDescription: 'No experience? No problem. Find entry level FIFO mining jobs in Australia.',
    ogImage: 'https://www.mineraljobs.com/og-entry-level.jpg',
    ogUrl: 'https://www.mineraljobs.com/entry-level/',
    twitterCard: 'summary_large_image',
  });
  return (
    <main>
    <div className="min-h-screen pt-16" style={{ background: "#111827" }}>
      {/* Hero */}
      <section className="relative py-20 px-4 sm:px-6 overflow-hidden" style={{ background: "linear-gradient(135deg, #111827 0%, #1a1f2e 50%, #2e1a0e 100%)" }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #f97316 0%, transparent 50%), radial-gradient(circle at 80% 20%, #b45309 0%, transparent 40%)" }}></div>
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 text-sm" style={{ borderColor: "rgba(249,115,22,0.3)", background: "rgba(249,115,22,0.08)", color: "#fb923c" }}>
            <HardHat size={14} /> <span style={{ fontFamily: "JetBrains Mono, monospace" }}>ENTRY LEVEL MINING — NO EXPERIENCE REQUIRED</span>
          </div>
          <h1 className="text-4xl sm:text-6xl font-black text-white mb-6 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Break Into Mining
            <br />
            <span style={{ color: "#f97316" }}>With Zero Experience</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
            Thousands of Australians enter the mining industry every year with no prior experience. Discover inexperienced mining roles, trainee positions, and how to land your first mine site job.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all hover:scale-105"
              style={{ background: "linear-gradient(135deg, #f97316, #ea580c)", color: "white" }}
            >
              Browse Entry Level Jobs <ArrowRight size={16} />
            </Link>
            <a
              href="#how-to-start"
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold border transition-all hover:bg-white/5"
              style={{ borderColor: "rgba(255,255,255,0.1)", color: "white" }}
            >
              How to Get Started
            </a>
          </div>
          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 mt-12 max-w-lg mx-auto">
            {[
              { value: "1,200+", label: "Entry Roles" },
              { value: "4 wks", label: "Avg Training" },
              { value: "$82K", label: "Avg Starting Pay" },
            ].map((s) => (
              <div key={s.label} className="bento-card p-4">
                <p className="text-2xl font-black text-white" style={{ fontFamily: "JetBrains Mono, monospace", color: "#f97316" }}>{s.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Entry Roles Grid */}
      <section className="py-16 px-4 sm:px-6 max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Inexperienced Mining Roles
        </h2>
        <p className="text-gray-400 mb-8">These roles require <strong className="text-white">no prior mining experience</strong> — just the right attitude.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {entryRoles.map((role) => (
            <div key={role.title} className="bento-card p-5 flex flex-col gap-4">
              <div className="text-3xl">{role.icon}</div>
              <div>
                <h3 className="font-bold text-white text-base mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{role.title}</h3>
                <p className="text-gray-400 text-sm">{role.description}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(249,115,22,0.15)", color: "#fb923c", fontFamily: "JetBrains Mono, monospace" }}>{role.salary}</span>
                {role.fifo ? <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(59,130,246,0.15)", color: "#60a5fa" }}>FIFO</span> : null}
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider" style={{ fontFamily: "JetBrains Mono, monospace" }}>Requirements</p>
                <ul className="space-y-1">
                  {role.requirements.map((r) => (
                    <li key={r} className="flex items-start gap-1.5 text-xs text-gray-400">
                      <span className="text-green-500 mt-0.5">✓</span> {r}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-auto pt-3 border-t border-white/5 flex items-center justify-between">
                <span className="text-xs text-gray-500">Training: <strong className="text-gray-300">{role.trainingTime}</strong></span>
                <Link to="/" className="text-xs font-semibold" style={{ color: "#f97316" }}>View Jobs →</Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How to Start */}
      <section id="how-to-start" className="py-16 px-4 sm:px-6" style={{ background: "#0f1623" }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 text-center" style={{ fontFamily: "Outfit, sans-serif" }}>
            How to Get Your First Mining Job
          </h2>
          <p className="text-gray-400 text-center mb-10">4 simple steps to mine site employment</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((s) => (
              <div key={s.step} className="bento-card p-5 relative">
                <span className="text-4xl font-black block mb-3" style={{ color: "rgba(249,115,22,0.2)", fontFamily: "JetBrains Mono, monospace" }}>{s.step}</span>
                <h3 className="font-bold text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{s.title}</h3>
                <p className="text-gray-400 text-sm mb-3">{s.desc}</p>
                <span className="text-xs" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>Cost: {s.cost}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 px-4 sm:px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            {[
              { icon: Shield, title: "Safe Start Program", desc: "All featured employers have verified safety programs for new starters." },
              { icon: Star, title: "Real Job Listings", desc: "Every listing is verified directly with the employer. No spam roles." },
              { icon: Wrench, title: "Training Pathways", desc: "Browse roles with clear Cert II/III training pathways included." },
            ].map((t) => (
              <div key={t.title} className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "rgba(249,115,22,0.1)" }}>
                  <t.icon size={22} style={{ color: "#f97316" }} />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{t.title}</h4>
                  <p className="text-gray-500 text-sm">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
    </main>
  );
}
